import { useState, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ListVideo, ClipboardPaste, Bot, SaveAll, Check, AlertCircle, ExternalLink } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { VideoResultCard } from "./VideoResultCard";
import { extractVideoId, streamSummary } from "./utils";
import { autoSaveSummary } from "./autoSave";
import type { VideoResult } from "./types";

const MODEL_OPTIONS = [
  { value: "auto", label: "🤖 Auto" },
  { value: "gemini-2.5-pro", label: "⚡ Gemini 2.5 Pro" },
  { value: "gemini-2.5-flash", label: "💡 Gemini 2.5 Flash" },
  { value: "gpt-5-mini", label: "💨 GPT-5 Mini" },
];

function extractPlaylistId(url: string): string | null {
  const m = url.match(/[?&]list=([a-zA-Z0-9_-]+)/);
  return m ? m[1] : null;
}

async function fetchPlaylistVideos(playlistId: string): Promise<string[]> {
  // Fetch playlist page and extract video IDs
  try {
    const resp = await fetch(`https://www.youtube.com/playlist?list=${playlistId}`);
    const html = await resp.text();
    const videoIds = new Set<string>();
    const regex = /"videoId"\s*:\s*"([a-zA-Z0-9_-]{11})"/g;
    let match;
    while ((match = regex.exec(html)) !== null) {
      videoIds.add(match[1]);
    }
    return Array.from(videoIds);
  } catch {
    return [];
  }
}

interface Props {
  userId?: string;
}

export function PlaylistMode({ userId }: Props) {
  const [url, setUrl] = useState("");
  const [model, setModel] = useState("auto");
  const [results, setResults] = useState<VideoResult[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isFetchingPlaylist, setIsFetchingPlaylist] = useState(false);
  const [savingAll, setSavingAll] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const { toast } = useToast();

  const updateResult = useCallback((id: string, patch: Partial<VideoResult>) => {
    setResults(prev => prev.map(r => r.id === id ? { ...r, ...patch } : r));
  }, []);

  const handleStart = async () => {
    const trimmed = url.trim();
    const playlistId = extractPlaylistId(trimmed);
    if (!playlistId) {
      toast({ title: "Invalid playlist URL", description: "Please enter a valid YouTube playlist URL.", variant: "destructive" });
      return;
    }

    setIsFetchingPlaylist(true);
    setResults([]);

    // Try fetching via edge function proxy to avoid CORS
    let videoIds: string[] = [];
    try {
      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/youtube-summarize`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({ url: trimmed, mode: "playlist" }),
        }
      );
      if (resp.ok) {
        const data = await resp.json();
        videoIds = data.videoIds || [];
      }
    } catch {}

    setIsFetchingPlaylist(false);

    if (videoIds.length === 0) {
      toast({ title: "No videos found", description: "Could not extract videos from this playlist. Try pasting individual URLs in Batch mode.", variant: "destructive" });
      return;
    }

    toast({ title: `Found ${videoIds.length} videos`, description: "Starting batch summarization…" });

    const items: VideoResult[] = videoIds.map((vid, i) => ({
      id: `pl-${Date.now()}-${i}`,
      url: `https://www.youtube.com/watch?v=${vid}`,
      videoId: vid,
      summary: "",
      status: "pending" as const,
    }));
    setResults(items);
    setIsProcessing(true);
    abortRef.current = new AbortController();

    for (const item of items) {
      if (abortRef.current.signal.aborted) break;
      setResults(prev => prev.map(r => r.id === item.id ? { ...r, status: "loading" } : r));
      try {
        const result = await streamSummary(
          item.url,
          (text) => setResults(prev => prev.map(r => r.id === item.id ? { ...r, summary: text } : r)),
          abortRef.current.signal,
          (meta) => setResults(prev => prev.map(r => r.id === item.id ? { ...r, author: meta.author, model: meta.model, language: meta.language } : r)),
          model,
        );
        const limitedInfo = /captions were unavailable|no captions available/i.test(result.summary);
        setResults(prev => prev.map(r => r.id === item.id ? { ...r, summary: result.summary, status: "done", limitedInfo, author: result.author, model: result.model, language: result.language } : r));
        // Auto-save
        const didSave = await autoSaveSummary({ userId, videoUrl: item.url, videoId: item.videoId, summaryContent: result.summary, author: result.author });
        if (didSave) {
          setResults(prev => prev.map(r => r.id === item.id ? { ...r, saved: true } : r));
        }
      } catch (error: any) {
        if (error.name === "AbortError") break;
        setResults(prev => prev.map(r => r.id === item.id ? { ...r, status: "error", error: error.message } : r));
      }
    }
    setIsProcessing(false);
  };

  const handleSaveAll = async () => {
    if (!userId) {
      toast({ title: "Sign in required", variant: "destructive" });
      return;
    }
    const unsaved = results.filter(r => r.status === "done" && !r.saved);
    if (unsaved.length === 0) return;
    setSavingAll(true);
    const rows = unsaved.map(r => ({
      user_id: userId,
      video_url: r.url,
      video_id: r.videoId,
      summary_content: r.summary,
      author: r.author || null,
    }));
    const { error } = await supabase.from("content_summaries").insert(rows);
    setSavingAll(false);
    if (error) {
      toast({ title: "Save failed", description: error.message, variant: "destructive" });
    } else {
      setResults(prev => prev.map(r => r.status === "done" ? { ...r, saved: true } : r));
      toast({ title: "All saved", description: `${unsaved.length} summaries saved.` });
    }
  };

  const completedCount = results.filter(r => r.status === "done").length;
  const unsavedCount = results.filter(r => r.status === "done" && !r.saved).length;

  return (
    <div className="space-y-6">
      <Card className="border-violet-500/10 bg-card/90 backdrop-blur-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <ListVideo className="h-5 w-5 text-violet-500" />
            <CardTitle className="text-lg">Playlist Summarizer</CardTitle>
          </div>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardDescription>
              Paste a YouTube playlist URL to summarize all videos automatically.
            </CardDescription>
            <div className="flex items-center gap-2">
              <Bot className="h-4 w-4 text-muted-foreground" />
              <Select value={model} onValueChange={setModel} disabled={isProcessing}>
                <SelectTrigger className="h-8 w-[160px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MODEL_OPTIONS.map(o => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              placeholder="https://www.youtube.com/playlist?list=..."
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              disabled={isProcessing || isFetchingPlaylist}
              className="flex-1"
            />
            <Button
              variant="outline"
              size="icon"
              disabled={isProcessing || isFetchingPlaylist}
              onClick={async () => {
                try {
                  const text = await navigator.clipboard.readText();
                  if (text.trim()) setUrl(text.trim());
                } catch {}
              }}
            >
              <ClipboardPaste className="h-4 w-4" />
            </Button>
            <Button
              onClick={handleStart}
              disabled={isProcessing || isFetchingPlaylist || !url.trim()}
              className="bg-violet-500 hover:bg-violet-600 text-white"
            >
              {isFetchingPlaylist ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Fetching…</>
              ) : isProcessing ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Processing…</>
              ) : (
                <><ListVideo className="h-4 w-4 mr-2" /> Summarize Playlist</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {results.length > 0 && (
        <>
          <div className="flex items-center gap-3">
            <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-violet-500 rounded-full transition-all duration-500"
                style={{ width: `${results.length > 0 ? (completedCount / results.length) * 100 : 0}%` }}
              />
            </div>
            <Badge variant="outline" className="shrink-0">
              {completedCount}/{results.length} done
            </Badge>
            {!isProcessing && unsavedCount > 0 && (
              <Button size="sm" onClick={handleSaveAll} disabled={savingAll} className="shrink-0">
                {savingAll ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <SaveAll className="h-4 w-4 mr-1" />}
                {savingAll ? "Saving…" : `Save All (${unsavedCount})`}
              </Button>
            )}
            {isProcessing && (
              <Button size="sm" variant="outline" onClick={() => abortRef.current?.abort()}>
                Cancel
              </Button>
            )}
          </div>

          <div className="space-y-4">
            {results.map(r => (
              <VideoResultCard
                key={r.id}
                result={r}
                userId={userId}
                onSaved={(id) => updateResult(id, { saved: true })}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
